import { useEffect, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Bounds, Grid } from "@react-three/drei";
import * as THREE from "three";
import { Loader2, RotateCw, AlertTriangle } from "lucide-react";
import { API, getToken } from "@/lib/api";

function toGeometries(meshes) {
  return meshes.map((m) => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(m.position, 3));
    if (m.normal) g.setAttribute("normal", new THREE.BufferAttribute(m.normal, 3));
    if (m.index) g.setIndex(new THREE.BufferAttribute(m.index, 1));
    if (!m.normal) g.computeVertexNormals();
    let color = new THREE.Color(0x8b98a6);
    if (m.color) color = new THREE.Color(m.color[0], m.color[1], m.color[2]);
    return { geometry: g, color };
  });
}

export const ModelViewer = ({ analysisId, filename }) => {
  const [geoms, setGeoms] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    let worker;
    let timer;
    (async () => {
      try {
        setStatus("loading");
        const _t = getToken();
        const resp = await fetch(`${API}/analyses/${analysisId}/model3d${_t ? `?token=${encodeURIComponent(_t)}` : ""}`);
        if (!resp.ok) throw new Error("fetch failed");
        const buf = await resp.arrayBuffer();
        if (!alive) return;

        worker = new Worker(new URL("./occtWorker.js", import.meta.url));
        timer = setTimeout(() => {
          if (alive) setStatus("error");
          if (worker) worker.terminate();
        }, 60000);

        worker.onmessage = (e) => {
          clearTimeout(timer);
          if (!alive) return;
          if (e.data.error || !e.data.meshes) {
            setStatus("error");
          } else {
            setGeoms(toGeometries(e.data.meshes));
            setStatus("ready");
          }
          worker.terminate();
        };
        worker.onerror = () => {
          clearTimeout(timer);
          if (alive) setStatus("error");
        };

        const isIges = /\.(iges|igs)$/i.test(filename || "");
        const origin = window.location.origin;
        worker.postMessage({
          buffer: buf,
          isIges,
          jsUrl: origin + "/occt-import-js.js",
          wasmUrl: origin + "/occt-import-js.wasm",
        }, [buf]);
      } catch (e) {
        if (alive) setStatus("error");
      }
    })();
    return () => {
      alive = false;
      clearTimeout(timer);
      if (worker) worker.terminate();
    };
  }, [analysisId, filename]);

  if (status === "loading") {
    return (
      <div data-testid="model-viewer-loading" className="flex h-[380px] flex-col items-center justify-center gap-3 rounded-sm border border-border bg-muted/20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="font-mono text-xs text-muted-foreground">Tessellating CAD geometry…</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div data-testid="model-viewer-error" className="flex h-[380px] flex-col items-center justify-center gap-3 rounded-sm border border-border bg-muted/20 px-6 text-center">
        <AlertTriangle className="h-6 w-6 text-chart-3" />
        <span className="text-sm text-muted-foreground">
          Could not render this 3D file in-browser. The geometry was still used for the analysis.
        </span>
      </div>
    );
  }

  return (
    <div data-testid="model-viewer" className="relative h-[380px] overflow-hidden rounded-sm border border-border bg-[#0d1526]">
      <div className="pointer-events-none absolute left-3 top-3 z-10 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/50">
        <RotateCw className="h-3 w-3" /> drag to orbit · scroll to zoom
      </div>
      <Canvas camera={{ position: [120, 90, 120], fov: 45, near: 0.1, far: 100000 }} dpr={[1, 2]}>
        <color attach="background" args={["#0d1526"]} />
        <ambientLight intensity={0.75} />
        <hemisphereLight args={["#ffffff", "#334155", 0.6]} />
        <directionalLight position={[80, 120, 80]} intensity={1.1} />
        <directionalLight position={[-60, -30, -80]} intensity={0.35} />
        <Bounds fit clip observe margin={1.25}>
          <group>
            {geoms.map((g, i) => (
              <mesh key={i} geometry={g.geometry}>
                <meshStandardMaterial color={g.color} metalness={0.35} roughness={0.55} />
              </mesh>
            ))}
          </group>
        </Bounds>
        <Grid
          args={[1000, 1000]}
          cellSize={10}
          cellColor="#1e2a44"
          sectionSize={50}
          sectionColor="#2c3e63"
          infiniteGrid
          fadeDistance={900}
          position={[0, -0.01, 0]}
        />
        <OrbitControls makeDefault enableDamping dampingFactor={0.1} />
      </Canvas>
    </div>
  );
};
